import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import {
  MapPin, Clock, Shield, CheckCircle, ArrowRight, Phone,
  Smartphone, Tablet, Monitor, ChevronRight, AlertCircle,
} from '../../lib/icons';
import { getPhoneRepairAreaBySlug, PHONE_REPAIR_AREAS } from '../../data/phone-repair-areas';
import SEOHead from '../../components/SEOHead';

const CORE_SERVICES = [
  { name: 'iPhone Screen Replacement', note: 'OLED quality, same-day most models' },
  { name: 'iPhone Battery Replacement', note: '12-month warranty' },
  { name: 'Samsung Screen Replacement', note: 'AMOLED panels, most Galaxy models' },
  { name: 'Samsung Battery Replacement', note: 'Quality cells, same-day' },
  { name: 'iPad Screen Replacement', note: 'All current iPad models' },
  { name: 'MacBook Battery & Screen', note: 'Air & Pro, all chip generations' },
  { name: 'Charging Port Repair', note: 'USB-C & Lightning' },
  { name: 'Data Recovery', note: 'iPhone, iPad, MacBook, SSD' },
  { name: 'Screen Protection', note: 'Tempered glass & ProtectionPro films' },
];

const PhoneRepairCityPage = () => {
  const { city } = useParams<{ city: string }>();
  const area = city ? getPhoneRepairAreaBySlug(city) : undefined;

  if (!area) return <Navigate to="/phone-repair" replace />;

  const otherAreas = PHONE_REPAIR_AREAS
    .filter(a => a.region === area.region && a.slug !== area.slug)
    .slice(0, 5);

  const localBizSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'GameFix Solutions — Phone Repair',
    description: `Premium iPhone, Samsung, and MacBook repair serving ${area.city}, ${area.state}.`,
    telephone: '+19512168534',
    email: 'info@gamefixsolutions.com',
    url: `https://gamefixsolutions.com/phone-repair/${area.slug}`,
    areaServed: [area.city, ...area.nearbyCities].map(c => ({ '@type': 'City', name: c })),
    serviceArea: {
      '@type': 'GeoCircle',
      geoMidpoint: { '@type': 'GeoCoordinates', latitude: area.lat, longitude: area.lng },
      geoRadius: '25000',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Phone & Apple Repair Services',
      itemListElement: [
        { '@type': 'Offer', name: 'iPhone Screen Replacement', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Samsung Screen Replacement', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'iPhone Battery Replacement', priceCurrency: 'USD', price: '99' },
      ],
    },
  };

  return (
    <div className="bg-[#050B14] min-h-screen font-sans">
      <SEOHead
        title={area.metaTitle}
        description={area.metaDescription}
        keywords={area.keywords.join(', ')}
        canonical={`https://gamefixsolutions.com/phone-repair/${area.slug}`}
        jsonLD={localBizSchema}
      />

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] to-[#050B14]" />
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: 'radial-gradient(ellipse at 30% 60%, rgba(14,165,233,0.2) 0%, transparent 60%)' }}
        />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-center gap-2 mb-3">
            <Link to="/phone-repair" className="text-slate-500 hover:text-[#0EA5E9] text-sm transition-colors">Phone Repair</Link>
            <ChevronRight className="h-3.5 w-3.5 text-slate-700" />
            <span className="text-slate-400 text-sm">{area.city}</span>
          </div>

          <div className="flex items-start gap-3 mb-4">
            <MapPin className="h-5 w-5 text-[#0EA5E9] mt-1 shrink-0" />
            <span className="text-[#0EA5E9] font-semibold">{area.city}, {area.state} · {area.county}</span>
          </div>

          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
            {area.heroHeading}
          </h1>
          <p className="text-slate-300 text-lg leading-relaxed mb-6 max-w-2xl">
            {area.heroSubheading}
          </p>

          {/* Turnaround badge */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8 ${
            area.isSameDay
              ? 'bg-[#10B981]/10 border-[#10B981]/30 text-[#10B981]'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
          }`}>
            <Clock className="h-4 w-4 shrink-0" />
            <span className="font-semibold text-sm">{area.turnaroundNote}</span>
          </div>

          <div className="flex flex-wrap gap-3">
            <Link to="/book-repair"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold transition-colors">
              Book a Repair <ArrowRight className="h-4 w-4" />
            </Link>
            <a href="tel:+19512168534"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-[#0EA5E9]/60 text-slate-300 hover:text-white font-semibold transition-colors">
              <Phone className="h-4 w-4" /> (951) 216-8534
            </a>
          </div>
        </div>
      </section>

      {/* ── Appointment Note (North County SD) ───────────────────────────────── */}
      {!area.isSameDay && (
        <div className="bg-amber-500/8 border-y border-amber-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-amber-300 font-semibold text-sm">Appointment-based service</p>
              <p className="text-slate-400 text-sm leading-relaxed mt-0.5">
                We're not a walk-in storefront in {area.city}. Turnaround time depends on appointment scheduling and parts availability.
                Contact us before booking to confirm timing and pricing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Two-column layout ─────────────────────────────────────────────────── */}
      <section className="py-14 lg:py-18">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Services list */}
            <div className="lg:col-span-2 space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-white mb-5">Services Available in {area.city}</h2>
                <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl overflow-hidden divide-y divide-slate-800/60">
                  {CORE_SERVICES.map(svc => (
                    <div key={svc.name} className="flex items-center gap-3 px-5 py-3.5">
                      <CheckCircle className="h-4 w-4 text-[#10B981] shrink-0" />
                      <div className="flex-1">
                        <span className="text-white text-sm font-medium">{svc.name}</span>
                        <span className="text-slate-500 text-xs ml-2">· {svc.note}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Local context */}
              <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-white font-bold text-lg mb-3">About {area.city} Service</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-3">{area.localIntro}</p>
                <p className="text-slate-400 text-sm leading-relaxed">{area.serviceNote}</p>
              </div>

              {/* Quality messaging */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-[#0A1628] border border-slate-700/50 rounded-xl p-5">
                  <Shield className="h-5 w-5 text-[#0EA5E9] mb-2" />
                  <h4 className="text-white font-semibold mb-1">Quality Parts</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    We use reliable, high-performance parts. No bargain components that cause dim screens or early failures.
                  </p>
                </div>
                <div className="bg-[#0A1628] border border-slate-700/50 rounded-xl p-5">
                  <Shield className="h-5 w-5 text-[#10B981] mb-2" />
                  <h4 className="text-white font-semibold mb-1">90-Day Warranty</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">
                    All repairs carry a 90-day warranty. Battery replacements are covered for 12 months.
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Book card */}
              <div className="bg-gradient-to-br from-[#0A1F3A] to-[#0A1628] border border-[#0EA5E9]/25 rounded-2xl p-6">
                <h3 className="text-white font-bold text-lg mb-1">Book a Repair</h3>
                <p className="text-slate-400 text-sm mb-4">
                  {area.isSameDay
                    ? 'Same-day service available by appointment.'
                    : 'Contact us first to confirm scheduling and availability.'}
                </p>
                <Link to="/book-repair"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-semibold text-sm transition-colors mb-3">
                  Book Online <ArrowRight className="h-4 w-4" />
                </Link>
                <a href="tel:+19512168534"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 font-semibold text-sm transition-colors">
                  <Phone className="h-4 w-4" /> (951) 216-8534
                </a>
              </div>

              {/* Quick facts */}
              <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-5 space-y-3">
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider">Quick Facts</h4>
                <div className="space-y-2.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Region</span>
                    <span className="text-slate-300">{area.county}</span>
                  </div>
                  {area.population && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Population</span>
                      <span className="text-slate-300">{area.population}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Turnaround</span>
                    <span className={`font-medium ${area.isSameDay ? 'text-[#10B981]' : 'text-amber-400'}`}>
                      {area.isSameDay ? 'Same day avail.' : 'By appointment'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Warranty</span>
                    <span className="text-slate-300">90 days min.</span>
                  </div>
                </div>
              </div>

              {/* Nearby areas */}
              {otherAreas.length > 0 && (
                <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-5">
                  <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-3">Nearby Areas</h4>
                  <div className="space-y-2">
                    {otherAreas.map(a => (
                      <Link
                        key={a.slug}
                        to={`/phone-repair/${a.slug}`}
                        className="flex items-center justify-between text-sm text-slate-400 hover:text-[#0EA5E9] transition-colors py-1"
                      >
                        <span>{a.city}</span>
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Device icons ─────────────────────────────────────────────────────── */}
      <section className="py-12 border-t border-slate-800/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-slate-500 text-sm uppercase tracking-widest mb-8">Devices We Service</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Smartphone, label: 'iPhone', sub: 'All generations' },
              { icon: Tablet, label: 'iPad', sub: 'All models' },
              { icon: Smartphone, label: 'Samsung', sub: 'Galaxy S & Z series' },
              { icon: Monitor, label: 'MacBook', sub: 'Air & Pro' },
            ].map(d => (
              <div key={d.label} className="bg-[#0A1628] border border-slate-800/60 rounded-xl p-5 text-center">
                <d.icon className="h-7 w-7 text-[#0EA5E9] mx-auto mb-2" />
                <p className="text-white font-semibold text-sm">{d.label}</p>
                <p className="text-slate-500 text-xs mt-0.5">{d.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer CTA ───────────────────────────────────────────────────────── */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Ready to get your device repaired?
          </h2>
          <p className="text-slate-400 mb-6">
            {area.isSameDay
              ? `Most repairs done same day in ${area.city}. Book your appointment and drop it off.`
              : `We'll work with your schedule in ${area.city}. Reach out first to confirm availability.`}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/book-repair"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0EA5E9] hover:bg-[#0284C7] text-white font-bold transition-colors">
              Book a Repair <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/phone-repair"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold transition-colors">
              All Phone Repair Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhoneRepairCityPage;
